import { ApiQueryParams } from '@frontend/api-utils';
import { HeatmapCellClickPayload } from '@frontend/chart/components';
import { ChartQueryParams, ChartTimerName } from '@frontend/chart/types';
import { EntityType } from '@frontend/common';
import { DefaultSecondaryButton, Spinner } from '@frontend/components/common';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { useActiveModal, useModals } from '@frontend/modal';
import { TransactionBadge } from '@frontend/transaction/badge';
import { ReactNode, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { InsightHeatmapCellSummaryRow, fetchInsightHeatmapCellSummary } from './fetch-insight-heatmap-cell-summary';
import { InsightHeatmapCellDrilldown } from './insight-heatmap-cell-drilldown';
import { InsightHeatmapCellTimeWindow } from './insight-heatmap-cell-window';

export interface InsightHeatmapCellSummaryModalProps {
    title: ReactNode;
    cell: HeatmapCellClickPayload;
    timeWindow: InsightHeatmapCellTimeWindow;
    drilldown: InsightHeatmapCellDrilldown;
    accountId: string;
    entityParams?: ApiQueryParams<ChartQueryParams>;
}

const timerLabel = (timerName: string | undefined): ReactNode => {
    if (timerName === ChartTimerName.TRANSACTION_DROP_OFF_PENDING) {
        return (
            <FormattedMessage
                id='platform.analytics.insights.heatmap-cell-summary.timer-drop-off'
                description='Label for a completed drop-off pending timer row in the insight heatmap cell summary pane'
                defaultMessage='Drop-off completed'
            />
        );
    }

    if (timerName === ChartTimerName.TRANSACTION_PICK_UP_PENDING) {
        return (
            <FormattedMessage
                id='platform.analytics.insights.heatmap-cell-summary.timer-pick-up'
                description='Label for a completed pick-up pending timer row in the insight heatmap cell summary pane'
                defaultMessage='Pick-up completed'
            />
        );
    }

    return timerName ?? '—';
};

const rowPrimaryLabel = (row: InsightHeatmapCellSummaryRow, kind: InsightHeatmapCellDrilldown['kind']): ReactNode => {
    if (kind === 'timers') {
        return timerLabel(row.timerName);
    }

    if (kind === 'packages') {
        return (
            <FormattedMessage
                id='platform.analytics.insights.heatmap-cell-summary.package-row'
                description='Primary label for a package row in the insight heatmap cell summary pane'
                defaultMessage='Package {id}'
                values={{ id: row.trackingNumber ?? row.id }}
            />
        );
    }

    return row.trackingNumber?.trim() || row.id;
};

const CellTitle = ({ cell }: { cell: HeatmapCellClickPayload }) => {
    const date = new Date(`${cell.date}T00:00:00.000Z`);

    if (typeof cell.hour === 'number') {
        const hourDate = new Date(`${cell.date}T${String(cell.hour).padStart(2, '0')}:00:00.000Z`);
        return (
            <FormattedMessage
                id='platform.analytics.insights.heatmap-cell-summary.title-hour'
                description='Title of the insight heatmap cell summary pane for an hour bucket'
                defaultMessage='{date} · {hour}'
                values={{
                    date: (
                        <FormattedDate
                            value={date}
                            day='numeric'
                            month='short'
                            year='numeric'
                            timeZone='UTC'
                        />
                    ),
                    hour: (
                        <FormattedTime
                            value={hourDate}
                            hour='2-digit'
                            minute='2-digit'
                            timeZone='UTC'
                        />
                    )
                }}
            />
        );
    }

    return (
        <FormattedDate
            value={date}
            weekday='short'
            day='numeric'
            month='short'
            year='numeric'
            timeZone='UTC'
        />
    );
};

export const InsightHeatmapCellSummaryModal = (props: InsightHeatmapCellSummaryModalProps) => {
    const { modalId } = useActiveModal();
    const { closeModal } = useModals();
    const { openPlatformDetail } = usePlatformModalHost();
    const [rows, setRows] = useState<InsightHeatmapCellSummaryRow[]>([]);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setHasError(false);

        fetchInsightHeatmapCellSummary(props.drilldown, {
            accountId: props.accountId,
            window: props.timeWindow,
            entityParams: props.entityParams
        })
            .then((result) => {
                if (!cancelled) {
                    setRows(result.rows);
                    setCount(result.count);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setHasError(true);
                    setRows([]);
                    setCount(0);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [props.accountId, props.drilldown, props.entityParams, props.timeWindow]);

    const close = () => closeModal(modalId);

    const onOpenTransaction = (row: InsightHeatmapCellSummaryRow) => {
        openPlatformDetail({
            entityType: row.entityType,
            id: row.id,
            context: { application: row.application }
        });
    };

    return (
        <div className='bg-background-surface flex h-full w-full flex-col gap-6 overflow-hidden p-6'>
            <div className='flex min-h-0 flex-1 flex-col gap-4 self-stretch'>
                <div className='flex w-full flex-row items-start justify-between gap-3'>
                    <div className='flex min-w-0 flex-col gap-1'>
                        <div className='text-foreground-default text-xl font-semibold'>
                            <CellTitle cell={props.cell} />
                        </div>
                        {props.title ? <div className='text-foreground-muted text-sm'>{props.title}</div> : null}
                    </div>
                    <ICONS.BUTTON.CLOSE
                        onClick={close}
                        className='text-foreground-default h-6 w-6 shrink-0 cursor-pointer'
                    />
                </div>

                {isLoading ? (
                    <div className='flex w-full flex-1 items-center justify-center py-8'>
                        <Spinner />
                    </div>
                ) : hasError ? (
                    <div className='text-foreground-default py-4'>{CommonMessage.ERRORS.FAILED_TO_LOAD}</div>
                ) : rows.length === 0 ? (
                    <div className='text-foreground-default py-4'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
                ) : (
                    <div className='flex min-h-0 w-full flex-1 flex-col gap-1 overflow-y-auto'>
                        {count > rows.length ? (
                            <div className='text-foreground-muted mb-1 text-xs'>
                                <FormattedMessage
                                    id='platform.analytics.insights.heatmap-cell-summary.showing-count'
                                    description='Caption under the insight heatmap cell summary list when results are truncated'
                                    defaultMessage='Showing {shown} of {total}'
                                    values={{ shown: rows.length, total: count }}
                                />
                            </div>
                        ) : null}
                        {rows.map((row) => (
                            <div
                                key={`${row.id}-${row.timerName ?? row.trackingNumber ?? ''}-${row.timestamp ?? ''}`}
                                className='flex w-full items-center justify-between gap-3 rounded-md px-3 py-2'>
                                <span className='flex min-w-0 flex-1 items-center gap-2'>
                                    <span className='text-foreground-default min-w-0 truncate text-sm font-medium'>
                                        {rowPrimaryLabel(row, props.drilldown.kind)}
                                    </span>
                                    {row.entityType === EntityType.TRANSACTION ? (
                                        <button
                                            type='button'
                                            className='text-brand-primary hover:bg-brand-primary hover:text-background-surface inline-flex shrink-0 cursor-pointer items-center rounded-sm px-1 text-sm font-semibold'
                                            onClick={() => onOpenTransaction(row)}>
                                            <TransactionBadge
                                                id={row.id}
                                                disableClick
                                                className='m-0 text-inherit'
                                            />
                                        </button>
                                    ) : null}
                                </span>
                                {row.timestamp ? (
                                    <span className='text-foreground-muted shrink-0 text-xs'>
                                        <FormattedTime
                                            value={new Date(row.timestamp)}
                                            hour='2-digit'
                                            minute='2-digit'
                                            second='2-digit'
                                            timeZone='UTC'
                                        />
                                    </span>
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className='flex w-full shrink-0 justify-end'>
                <DefaultSecondaryButton
                    onClick={close}
                    text={CommonMessage.BUTTONS.CLOSE}
                />
            </div>
        </div>
    );
};
